import { useState, type ReactElement } from 'react';
import confetti from 'canvas-confetti';

import { useOrganisationFeaturesOf } from 'app/hooks/useOrganisationFeatures';
import useSelectedPharmacy from 'app/hooks/useSelectedPharmacy';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

import { formatPlusPrice } from './plusPlanPrice';
import usePlusPlanPrice, { type PlusPlanPrice } from './usePlusPlanPrice';
import useRequestPlusUpgrade, { type PlusUpgradeRequestResult } from './useRequestPlusUpgrade';

interface RequestPlusModalProps {
  open: boolean;
  onClose: () => void;
  organisationId?: string;
}

function celebrateUpgradeRequest() {
  confetti({ particleCount: 150, spread: 90, origin: { y: 0.6 }, disableForReducedMotion: true });
}

const PRIMARY_BUTTON_CLASS =
  'vtwo:w-full vtwo:bg-teal-900 vtwo:text-white vtwo:hover:bg-teal-950 vtwo:rounded-lg';

interface RequestStepProps {
  price: PlusPlanPrice;
  /** Sending the request, or not known yet whether the organisation already asked. */
  isBusy: boolean;
  onRequest: () => void;
  onCancel: () => void;
}

function RequestStep({ price, isBusy, onRequest, onCancel }: RequestStepProps): ReactElement {
  return (
    <div className="vtwo:flex vtwo:flex-col vtwo:md:flex-row">
      <div className="vtwo:flex-1 vtwo:flex vtwo:flex-col vtwo:items-center vtwo:gap-6 vtwo:p-10 vtwo:text-center">
        <DialogTitle className="vtwo:text-3xl vtwo:font-bold vtwo:leading-tight">
          Upgrade plan to Healthera <span className="vtwo:text-featured">Plus+</span>
        </DialogTitle>
        <div>
          <p className="vtwo:text-3xl vtwo:font-bold">{formatPlusPrice(price.monthly)} /mo</p>
          <p className="vtwo:text-sm vtwo:text-gray-600">
            + {formatPlusPrice(price.setUpFee)} set-up fee
          </p>
        </div>
        <DialogDescription className="vtwo:text-base vtwo:text-black">
          Billing changes from your upgrade date.
        </DialogDescription>
        <div className="vtwo:flex vtwo:flex-col vtwo:gap-3 vtwo:w-full">
          <Button size="xl" className={PRIMARY_BUTTON_CLASS} disabled={isBusy} onClick={onRequest}>
            Request Upgrade
          </Button>
          <Button
            variant="outline"
            size="xl"
            className="vtwo:w-full vtwo:rounded-lg"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </div>
      </div>
      <div
        aria-hidden="true"
        className="vtwo:w-full vtwo:md:w-2/5 vtwo:shrink-0 vtwo:flex vtwo:items-end vtwo:p-8 vtwo:min-h-[220px] vtwo:bg-[radial-gradient(ellipse_at_bottom_right,#F97316_0%,#0F766E_45%,#0B1120_80%)]"
      >
        <p className="vtwo:text-3xl vtwo:font-bold vtwo:leading-tight vtwo:text-transparent vtwo:bg-clip-text vtwo:bg-linear-to-r vtwo:from-teal-200 vtwo:to-orange-300">
          Grow and automate your pharmacy
        </p>
      </div>
    </div>
  );
}

interface ReceivedStepProps {
  alreadyRequested: boolean;
  onDone: () => void;
}

function ReceivedStep({ alreadyRequested, onDone }: ReceivedStepProps): ReactElement {
  return (
    <div className="vtwo:flex vtwo:flex-col vtwo:items-center vtwo:gap-6 vtwo:px-10 vtwo:py-16 vtwo:text-center">
      <DialogTitle className="vtwo:text-3xl vtwo:font-bold vtwo:leading-tight">
        Upgrade request received
      </DialogTitle>
      <DialogDescription className="vtwo:text-base vtwo:text-black vtwo:max-w-md">
        {alreadyRequested
          ? "We already have your upgrade request. We'll be in touch to confirm and switch your plan."
          : "Thanks — we've received your upgrade request. We'll be in touch to confirm and switch your plan."}
      </DialogDescription>
      <Button size="xl" className={`${PRIMARY_BUTTON_CLASS} vtwo:max-w-sm`} onClick={onDone}>
        Done
      </Button>
    </div>
  );
}

export default function RequestPlusModal({
  open,
  onClose,
  organisationId: organisationIdProp,
}: RequestPlusModalProps): ReactElement {
  const [received, setReceived] = useState<PlusUpgradeRequestResult | null>(null);
  const price = usePlusPlanPrice();
  const selectedPharmacy = useSelectedPharmacy();
  const organisationId = organisationIdProp ?? selectedPharmacy?.chain_id ?? '';
  const { mutate: requestUpgrade, isPending } = useRequestPlusUpgrade(organisationId);
  // Reached from a locked card or a refused action after the organisation already asked for Plus:
  // there is nothing left to request.
  const { plusUpgradeRequested, isLoading: checkingRequest } =
    useOrganisationFeaturesOf(organisationId);

  function handleRequest() {
    requestUpgrade(undefined, {
      onSuccess: (result) => {
        setReceived(result);
        if (!result.alreadyRequested) celebrateUpgradeRequest();
      },
    });
  }

  function handleClose() {
    setReceived(null);
    onClose();
  }

  let content = (
    <RequestStep
      price={price}
      isBusy={isPending || checkingRequest}
      onRequest={handleRequest}
      onCancel={handleClose}
    />
  );
  if (received || plusUpgradeRequested) {
    content = (
      <ReceivedStep alreadyRequested={received?.alreadyRequested ?? true} onDone={handleClose} />
    );
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent
        showCloseButton={false}
        className="vtwo:p-0 vtwo:gap-0 vtwo:overflow-hidden vtwo:rounded-2xl vtwo:sm:max-w-[800px]"
      >
        {content}
      </DialogContent>
    </Dialog>
  );
}
